// ANALYSIS AND CHANGES:
// This is a new custom hook that wraps all of the application form logic.
// RESPONSABILIDADES:
// 1. Manage the application modal state (`isApplicationModalOpen`).
// 2. Track which position was selected for the application (`selectedPositionForApplication`).
// 3. Manage the thank-you modal state (`isThankYouVisible`).
// 4. Track the resume file state (`resumeFile`).
// 5. Hold the form submit logic (`onFinish`) and the upload handler (`handleResumeUpload`).
// BENEFITS:
// - Fully isolates the form logic, letting `JobApplicationModal` be a pure UI component.
// - Makes it easier to test and change the application flow without affecting other parts of the system.

import { useState } from 'react';
import { useJobApplications } from '@hooks/useJobApplications';
import { Position } from '@common';
import { Form, message } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import { validateFileFormat } from '@helpers';

interface FormValues {
  fullName: string;
  email: string;
  phone: string;
  message: string;
}

export const useApplicationForm = () => {
  const { createJobApplication } = useJobApplications();
  const [form] = Form.useForm();

  const [isApplicationModalOpen, setIsApplicationModalOpen] = useState(false);
  const [selectedPositionForApplication, setSelectedPositionForApplication] = useState<Position | null>(null);
  const [isThankYouVisible, setIsThankYouVisible] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);

  const handleOpenApplicationModal = (position: Position) => {
    setSelectedPositionForApplication(position);
    setIsApplicationModalOpen(true);
  };

  const handleCloseApplicationModal = () => {
    setIsApplicationModalOpen(false);
    form.resetFields();
    setResumeFile(null);
    // Delay so the application modal content disappears before the thank-you modal shows
    setTimeout(() => setSelectedPositionForApplication(null), 300);
  };

  const handleCloseThankYouModal = () => {
    setIsThankYouVisible(false);
  };
  
  const handleResumeUpload = (file: UploadFile) => {
    if (validateFileFormat(file)) {
      setResumeFile(file as unknown as File);
    }
    return false; // Prevents the automatic Ant Design upload
  };

  const onFinish = (values: FormValues) => {
    if (!resumeFile) {
      message.error("Please attach your resume");
      return;
    }
    if (!selectedPositionForApplication) {
      message.error("An error occurred. Please select the position again.");
      return;
    }

    createJobApplication.mutate(
      {
        data: { ...values, positionId: selectedPositionForApplication.id },
        resume: resumeFile,
      },
      {
        onSuccess: () => {
          handleCloseApplicationModal();
          // Delay so the application modal can close before the thank-you modal opens
          setTimeout(() => setIsThankYouVisible(true), 500);
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.message || "Unable to submit your application.";
          message.error(errorMessage);
        },
      }
    );
  };

  return {
    isApplicationModalOpen,
    isThankYouVisible,
    selectedPositionForApplication,
    handleOpenApplicationModal,
    handleCloseApplicationModal,
    handleCloseThankYouModal,
    applicationFormProps: {
      form,
      onFinish,
      handleResumeUpload,
      isLoading: createJobApplication.isPending,
      resumeFile,
    },
  };
};
